<template>
  <div class="leftCardContainer">
    <div class="rightSideTitleActions">
      <span class="leftTitle">{{ $t('NetworkRules') }}</span>
      <div class="actionsContainer">
        <div class="searchContainer">
          <v-icon> search </v-icon>
          <InputField
            inputType="text"
            :value="search"
            :placeholder="$t('Search')"
            :inputStyle="{
              borderRadius: '0px',
              width: '130px',
              borderTop: 'none',
              borderRight: 'none',
              borderLeft: 'none',
              borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
            }"
            @input="handleSearch($event)" />
        </div>
        <CustomButton
          icon="delete"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :disabled="!selectedObjectID || !userHasPermission('network_rule_destroy')"
          :buttonStyling="{ padding: '8px', marginRight: '5px', borderRadius: '4px' }"
          @buttonClick="openDeleteDialog" />
        <CustomButton
          icon="add"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="{ padding: '8px', marginRight: '5px', borderRadius: '4px' }"
          :disabled="userIsCreatingOrEditingData  || !userHasPermission('network_rule_store')"
          @buttonClick="handleAdd"
        />
      </div>
    </div>
    <div class="tableDivContainer">
      <Table
        :checkbox="false"
        :loading="loading"
        :headers="headers"
        :items="networkRules"
        :sortable="true"
        :emitRowClickBusEvent="true"
        @onScrollBottom="onScrollBottom"
        @onScrollTop="onScrollTop"
        @sortClick="onSortClick"
      />
    </div>
    <dialog-choice
      :isOpen="deleteDialogIsOpen"
      :title="$t('DeleteNetworkRule')"
      :content="$t('DeleteNetworkRuleConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="handleDelete"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="deleteDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="#29BDF0"
    />
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="#29BDF0"
      style="position: absolute;"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import ManagementPage from "@/newComponents/_Organisms/ManagementPage.vue";
import Table from "@/newComponents/_Molecules/Table.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import InputField from "@/newComponents/_Atoms/InputField.vue";
import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
import LoadingSpinner from "vue-loading-overlay";
import DialogChoice from '@/newComponents/_Molecules/DialogChoice.vue';

export default {
  name: "TableView",

  mixins: [managementDatatableMixin, checkPermissionsMixin],

  components: {
    Table,
    ManagementPage,
    CustomButton,
    InputField,
    DialogContent,
    LoadingSpinner,
    DialogChoice,
  },

  props: {
    selectedObjectID: {
      type: Number,
    },
  },

  data() {
    return {
      dialog: false,
      deleteDialogIsOpen: false,
      value: null,
      type: "normal",
      search: null,
      loading: false,
      scrollTop: 0,
      sortOrder: null,
      scrollHeight: 0,
      pageNumber: 1,
      pagination: 25,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      networkRules: [],
      permissionName: "network_rule",
      userIsCreatingOrEditingData: false,
    };
  },

  mounted() {
    this.getInitialNetworkRules();
    bus.$on("reload-network-rules", () => this.getInitialNetworkRules())
    bus.$on("toggle-create-edit-flag", () => this.userIsCreatingOrEditingData = !this.userIsCreatingOrEditingData)
  },

  destroyed() {
    bus.$off("reload-network-rules");
    bus.$off("toggle-create-edit-flag");
  },

  computed: {
    headers() {
      return [
        { text: this.$i18n.tc("Label", 1), varName: "name" },
        { text: this.$i18n.t("Description"), varName: "description" },
        { text: this.$i18n.t("CreatedAt"), varName: "created_at" },
        { text: this.$i18n.t("UpdatedAt"), varName: "updated_at" },
      ]
    },
  },

  methods: {
    ...mapActions(["FETCH_ALL_NETWORK_RULES_TEMPLATES", "DELETE_NETWORK_RULES_TEMPLATE"]),

    closeDialog() {
      this.dialog = false;
    },

    async getInitialNetworkRules() {
      this.loading = true;
      this.currentPageBot = 1
      this.currentPageTop = 1
      let queryParams = {
        "page[number]": 1,
        paginate: 25,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getNetworkRules(queryParams);
    },

    getNetworkRulesBottom() {
      this.loading = true;
      this.pagination = 25;
      if (this.networkRules.length >= 75) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      let queryParams = {
        "page[number]": this.currentPageBot,
        paginate: this.pagination,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getNetworkRules(queryParams, 'bottom');
    },

    async getNetworkRulesTop() {
      this.loading = true;
      this.pagination = 25;
      this.currentPageTop--;

      let queryParams = {
        "page[number]": this.currentPageTop,
        paginate: this.pagination,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getNetworkRules(queryParams, 'top');
    },

    async getNetworkRules(queryParams = null, scrollDirection = null) {
      this.loading = true;
      try {
        let networkRules = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "type",
          value: "Calculation",
          queryParams: queryParams,
        });
        this.processNetworkRulesData(networkRules, scrollDirection);

      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    processNetworkRulesData(networkRulesData, scrollDirection = null) {
      let allNetworkRules = null;
      let currentScroll = null;
      let rowElement = null;
      switch (scrollDirection) {
        case 'top':
          this.lastPage = networkRulesData.meta.last_page;
          allNetworkRules = [...networkRulesData.data, ...this.networkRules];
          this.networkRules = Array.from(new Set(allNetworkRules.map(a => a.id)))
            .map(id => { return allNetworkRules.find(a => a.id === id) })
          if (this.networkRules.length > 75) {
            this.networkRules.splice(-(this.networkRules.length - 75));
            this.currentPageBot--;
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 25; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll + offset,
                behavior: "smooth",
              });
            }
          }
          break;
        case 'bottom':
          this.lastPage = networkRulesData.meta.last_page;
          allNetworkRules = [...this.networkRules, ...networkRulesData.data];
          this.networkRules = Array.from(new Set(allNetworkRules.map(a => a.id)))
            .map(id => { return allNetworkRules.find(a => a.id === id) })
          if (this.networkRules.length > 75) {
            this.networkRules = this.networkRules.slice(this.networkRules.length - 75);
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 25; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll - offset,
                behavior: "smooth",
              });
            }
          }
          break;
        default:
          this.networkRules = networkRulesData.data;
          this.lastPage = networkRulesData.meta.last_page;
          break;
      }
    },

    handleSearch: _.debounce(function (event) {
      if (event === "") {
        this.search = null;
      } else {
        this.search = event;
      }
      this.networkRules = [];
      this.getInitialNetworkRules();
    }, 1000),

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getNetworkRulesBottom();
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getNetworkRulesTop();
      }
    },

    onSortClick(sortOrder, index) {
      this.networkRules = []
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.getInitialNetworkRules();
    },

    handleAdd() {
      this.$emit('open-create-network-rule-form')
    },

    handleTabChange(index) {
      this.activeTab = index;
      bus.$emit("clear-SelectedItems")
      bus.$emit("clear-selected-row")
      this.$emit('clear-selected-item')
      this.getInitialNetworkRules();
    },

    openDeleteDialog(){
      if (this.selectedObjectID) {
        this.deleteDialogIsOpen = true;
      }
    },

    async handleDelete() {
      try {
        this.loading = true;
        await this.DELETE_NETWORK_RULES_TEMPLATE(this.selectedObjectID); // uuid
        this.getInitialNetworkRules();
        bus.$emit("clear-SelectedItems")
        bus.$emit("clear-selected-row")
        this.$emit('clear-selected-item')
      } catch (e) {
        let response = e.response.data.error_message
          ? e.response.data.error_message
          : e.response.message;
        this.$toasted.global.toast_error(response);
      } finally {
        this.deleteDialogIsOpen = false
        this.loading = false;
      }
    }
  },
};
</script>

<style scoped>
.searchContainer {
  display: flex;
  align-items: center;
}

.tableDivContainer {
  height: 87%;
  margin-top: 5px;
}

.tabsContainer {
  height: 8%;
  display: flex;
  margin-top: 5px;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
}

.rightSideTitleActions {
  height: 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.buttons-container-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 20px;
}

.cancel-button-custom {
  background: transparent;
  border-radius: 0.25rem;
  border: 0.609px solid #29bdf0;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  /* 200% */
  text-transform: uppercase;
}

.rightSideTitleActions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.layer-search {
  flex: 0 0 auto;
  font-size: 12px;
  margin-right: 5px;
}

.layers-search .v-input__slot {
  min-height: 0 !important;
}

.layers-search .v-label {
  top: 4px !important;
  font: normal normal normal 12px/16px Segoe UI;
  color: #b1b6b9;
}

.span-custom {
  display: flex;
  padding: 0px 20px;
  color: #767676;
  font-family: Lato;
  font-size: 0.625rem;
  font-style: normal;
  font-weight: 400;
  line-height: 0.9375rem;
}

.tabs-custom {
  padding: 0px 20px;
}

.table-custom {
  height: 90%;
}

.table-buttons-custom {
  height: 70%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemSettingsManagement/NetworkRules/TableView.vue